import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

interface PersonalBest {
    time_formatted: string;
    time_seconds: number;
    pace_per_km: string;
    start_date_local: string;
}

interface PersonalBests {
    [key: string]: PersonalBest;
}

const STRAVA_API_BASE = 'https://ai-reply-bot.vercel.app/api/strava/personal-bests';
const STRAVA_CLIENT_ID = '191703';

const getStravaAuthUrl = (redirectPath: string = '/strava') => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://cabhinav.com';
    return `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(origin)}&state=${encodeURIComponent(redirectPath)}&approval_prompt=force&scope=read,activity:read`;
};

const MinimalStrava: React.FC = () => {
    const [personalBests, setPersonalBests] = useState<PersonalBests | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [setupRequired, setSetupRequired] = useState(false);

    // Exchange authorization code for token
    const exchangeAuthCode = useCallback(async (code: string) => {
        try {
            const response = await fetch(STRAVA_API_BASE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: code })
            });
            const data = await response.json();
            if (data.success) {
                localStorage.setItem('stravaAuthorized', 'true');
                localStorage.setItem('stravaAuthTime', Date.now().toString());
                return true;
            }
            return false;
        } catch {
            return false;
        }
    }, []);

    const loadPersonalBests = useCallback(async (forceRefresh = false) => {
        setLoading(true);
        setError(null);
        try {
            const url = forceRefresh ? `${STRAVA_API_BASE}?refresh=true` : STRAVA_API_BASE;
            const response = await fetch(url);

            if (response.status === 401) {
                localStorage.removeItem('stravaAuthorized');
                localStorage.removeItem('stravaAuthTime');
                setSetupRequired(true);
                setError('Authentication required');
                return;
            }

            if (!response.ok) throw new Error('Failed to fetch data');

            const data = await response.json();
            if (data.success) {
                setPersonalBests(data.data.personal_bests);
                setSetupRequired(false);
            } else if (data.setup_required) {
                setSetupRequired(true);
                setError('Please connect your Strava account');
            } else {
                setError(data.message || 'Failed to load data');
            }
        } catch (err) {
            setError('Error loading Strava data');
        } finally {
            setLoading(false);
        }
    }, []);

    // Handle OAuth callback and refresh parameter
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const authParam = urlParams.get('auth');
        const authCode = urlParams.get('code');
        const authError = urlParams.get('error');
        const refreshParam = urlParams.get('refresh');

        // If ?auth=1, redirect to Strava OAuth immediately
        if (authParam === '1') {
            window.location.href = getStravaAuthUrl('/strava');
            return;
        }

        if (authError) {
            window.history.replaceState({}, document.title, window.location.pathname);
            setSetupRequired(true);
            setError('Authorization failed');
            setLoading(false);
            return;
        }

        if (authCode) {
            const processedCodes = JSON.parse(localStorage.getItem('processedAuthCodes') || '[]');
            if (!processedCodes.includes(authCode)) {
                exchangeAuthCode(authCode).then((success) => {
                    processedCodes.push(authCode);
                    localStorage.setItem('processedAuthCodes', JSON.stringify(processedCodes.slice(-5)));
                    window.history.replaceState({}, document.title, window.location.pathname);
                    if (success) {
                        loadPersonalBests();
                    } else {
                        setSetupRequired(true);
                        setError('Authorization failed');
                        setLoading(false);
                    }
                });
            } else {
                window.history.replaceState({}, document.title, window.location.pathname);
                loadPersonalBests();
            }
            return;
        }

        // Handle refresh parameter
        const forceRefresh = refreshParam === 'true' || refreshParam === '1';
        if (forceRefresh) {
            urlParams.delete('refresh');
            const newUrl = urlParams.toString()
                ? `${window.location.pathname}?${urlParams.toString()}`
                : window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
        }

        loadPersonalBests(forceRefresh);
    }, [exchangeAuthCode, loadPersonalBests]);

    const distances = [
        { key: '400m', label: '400m' },
        { key: 'half_mile', label: 'Half Mile' },
        { key: '1km', label: '1K' },
        { key: '1_mile', label: '1 Mile' },
        { key: '2_mile', label: '2 Mile' },
        { key: '5km', label: '5K' },
        { key: '10km', label: '10K' },
        { key: '15km', label: '15K' },
        { key: '10_mile', label: '10 Mile' },
        { key: '20km', label: '20K' },
        { key: 'half_marathon', label: 'Half Marathon' },
        { key: 'marathon', label: 'Marathon' },
    ];

    return (
        <div className="space-y-8">
            <Helmet>
                <title>Strava Activities | Abhinav Chinnusamy</title>
            </Helmet>

            <header>
                <h1 className="text-xl font-bold uppercase tracking-widest">/strava</h1>
                <p className="mt-2 text-sm text-gray-600">Running performance metrics.</p>
            </header>

            {loading ? (
                <div className="text-sm text-gray-400">[ loading data... ]</div>
            ) : setupRequired ? (
                <div className="space-y-4">
                    <div className="text-sm text-gray-600">
                        {error || 'Connect your Strava account to view personal bests'}
                    </div>
                    <a
                        href={getStravaAuthUrl('/strava')}
                        className="inline-block text-sm underline hover:no-underline"
                    >
                        [ connect strava ]
                    </a>
                </div>
            ) : error ? (
                <div className="text-sm text-red-500">[ error: {error} ]</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm">
                    <section className="space-y-4">
                        <h2 className="font-bold border-b border-black pb-2">Personal Bests</h2>
                        <div className="space-y-3">
                            {distances.map(({ key, label }) => {
                                const pb = personalBests?.[key];
                                return (
                                    <div key={key} className="space-y-1">
                                        <div className="flex justify-between items-baseline">
                                            <span className="font-medium">{label}</span>
                                            {pb ? (
                                                <div className="text-right">
                                                    <span>{pb.time_formatted}</span>
                                                    <span className="text-gray-500 ml-2 text-xs">({pb.pace_per_km}/km)</span>
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">--:--</span>
                                            )}
                                        </div>
                                        {pb && (
                                            <div className="text-[10px] text-gray-400 text-right font-mono">
                                                {new Date(pb.start_date_local).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    <section className="space-y-4">
                        <h2 className="font-bold border-b border-black pb-2">Recent Activity</h2>
                        <div className="text-gray-500 italic">
                            [ Check <a href="https://www.strava.com/athletes/99464226" target="_blank" rel="noreferrer" className="underline hover:text-black">Strava Profile</a> for full history ]
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default MinimalStrava;
